import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Group,
  LoadingOverlay,
  Menu,
  Paper,
  Stack,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconCalendarStar,
  IconPackage,
  IconPlus,
} from "@tabler/icons-react";
import { useSalon } from "../../context/SalonContext";
import { useApp } from "../../context/AppContext";
import { useFeatureAccess } from "../../context/FeatureAccessContext";
import usePermissions from "../../hooks/usePermissions";
import { useSalonScopedFetch } from "../../hooks/useSalonScopedFetch";
import { SalonContextType } from "../../types/context";
import {
  CreateServicePackageRequest,
  PackageKind,
  PackageTemplateType,
  ServicePackage,
} from "../../types/packages";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import PackageCard from "./packages/PackageCard";
import PackageEditorModal from "./packages/PackageEditorModal";
import { classifySpecial, SpecialStatus } from "./packages/specialDates";

type LibraryTab = "packages" | "specials";

/** Offer-type chooser for the per-tab Add menu (two-line Menu.Items). */
const ADD_OPTIONS: ReadonlyArray<{
  type: PackageTemplateType;
  label: string;
  description: string;
}> = [
  {
    type: "bogo_percent",
    label: "Buy X, Get Y % Off",
    description:
      "Reward buying one service with a discount (or free) on another.",
  },
  {
    type: "simple_discount",
    label: "Simple or Bundled Discount",
    description:
      "Pick any services and quantities, then discount by a flat amount or percentage.",
  },
];

interface EditorState {
  kind: PackageKind;
  offerType: PackageTemplateType;
  initialPackage?: ServicePackage;
  duplicateSource?: ServicePackage;
}

const SPECIAL_SECTIONS: ReadonlyArray<{
  status: SpecialStatus;
  label: string;
}> = [
  { status: "active", label: "Active" },
  { status: "upcoming", label: "Upcoming" },
  { status: "past", label: "Past" },
];

// Load failures get a Retry button (re-arms the one-shot scoped fetch);
// save/archive failures do not — retrying those means re-submitting the form.
const LOAD_ERROR = "Failed to load service packages.";

const PackagesSettingsTab = () => {
  const { businessInfo, services } = useSalon() as SalonContextType;
  const { businessVertical } = useFeatureAccess();
  const perms = usePermissions();
  const servicesPerms = perms?.settingsServices;
  // Edit allowed unless services management is explicitly denied (fail-open
  // only when permissions haven't loaded, matching other settings tabs).
  const canEdit = servicesPerms?.canManageServices !== false;
  const {
    getServicePackages,
    createServicePackage,
    updateServicePackage,
    deleteServicePackage,
  } = useApp();

  const [packages, setPackages] = useState<ServicePackage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<LibraryTab>("packages");
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [archivingPackage, setArchivingPackage] =
    useState<ServicePackage | null>(null);
  const [error, setError] = useState<string | null>(null);

  const salonId = businessInfo?.id ?? "";

  const fetchPackages = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const list = await getServicePackages(salonId);
      setPackages(list);
    } catch (err) {
      console.error("Error fetching service packages:", err);
      setError(LOAD_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch packages ONCE per salon (multi-location loop fix): the guard also
  // covers a flapping salonId (A→B→A refires nothing) and a failed fetch
  // (backend 403 on a second location) stops at the error Alert instead of
  // refetching on every identity churn. Mutations update local state directly;
  // the error Alert's Retry re-arms the load explicitly.
  const { retry: retryPackages } = useSalonScopedFetch(salonId, fetchPackages, {
    tag: "service-packages",
  });

  const packagesList = useMemo(
    () => packages.filter((pkg) => pkg.kind !== "special"),
    [packages],
  );
  const specialsList = useMemo(
    () => packages.filter((pkg) => pkg.kind === "special"),
    [packages],
  );
  const specialsByStatus = useMemo(() => {
    const grouped: Record<SpecialStatus, ServicePackage[]> = {
      active: [],
      upcoming: [],
      past: [],
    };
    specialsList.forEach((pkg) =>
      grouped[classifySpecial(pkg, businessInfo?.timezone)].push(pkg),
    );
    return grouped;
  }, [specialsList, businessInfo?.timezone]);

  const handleEditorSubmit = async (data: CreateServicePackageRequest) => {
    if (!editor) return;
    try {
      setIsSaving(true);
      setError(null);
      if (editor.initialPackage) {
        const updated = await updateServicePackage(
          salonId,
          editor.initialPackage.id,
          data,
        );
        if (updated) {
          const editedId = editor.initialPackage.id;
          setPackages((prev) =>
            prev.map((pkg) =>
              pkg.id === editedId ? { ...pkg, ...updated } : pkg,
            ),
          );
          setEditor(null);
        }
      } else {
        // Create + duplicate both save a NEW record.
        const created = await createServicePackage(salonId, data);
        if (created) {
          setPackages((prev) => [...prev, created]);
          setEditor(null);
        }
      }
    } catch (err) {
      console.error("Error saving service package:", err);
      setError("Failed to save. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!archivingPackage) return;
    try {
      setIsSaving(true);
      setError(null);
      await deleteServicePackage(salonId, archivingPackage.id);
      setPackages((prev) =>
        prev.filter((pkg) => pkg.id !== archivingPackage.id),
      );
      setArchivingPackage(null);
    } catch (err) {
      console.error("Error archiving service package:", err);
      setError("Failed to archive. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const kindForTab: PackageKind =
    activeTab === "specials" ? "special" : "package";

  const openCreate = (offerType: PackageTemplateType) =>
    setEditor({ kind: kindForTab, offerType });

  const openEdit = (pkg: ServicePackage) =>
    setEditor({
      kind: pkg.kind ?? "package",
      offerType: pkg.package_type ?? "simple_discount",
      initialPackage: pkg,
    });

  const openDuplicate = (pkg: ServicePackage) =>
    setEditor({
      kind: pkg.kind ?? "package",
      offerType: pkg.package_type ?? "simple_discount",
      duplicateSource: pkg,
    });

  const packagesDescription = `Enable clients to pre-purchase multiple services${
    businessVertical === "med_spa" ? " or banked units/syringes" : ""
  } to be redeemed at a future date.`;
  const specialsDescription =
    "Run time-boxed promotions to enable clients to purchase one or more services at a discounted price.";

  const handleTabChange = (next: string | null) => {
    if (next === "packages" || next === "specials") {
      setActiveTab(next);
    }
  };

  const renderCard = (pkg: ServicePackage, dimmed = false) => (
    <PackageCard
      key={pkg.id}
      pkg={pkg}
      services={services || []}
      canEdit={canEdit}
      dimmed={dimmed}
      onEdit={openEdit}
      onDuplicate={openDuplicate}
      onArchive={setArchivingPackage}
    />
  );

  const renderEmptyState = (message: string) => (
    <Paper p="xl" withBorder>
      <Stack align="center" gap="md">
        {activeTab === "specials" ? (
          <IconCalendarStar size={48} color="gray" />
        ) : (
          <IconPackage size={48} color="gray" />
        )}
        <Text size="sm" c="dimmed" ta="center">
          {message}
        </Text>
      </Stack>
    </Paper>
  );

  const renderPackagesTab = () =>
    packagesList.length === 0 ? (
      renderEmptyState(
        "No packages yet. Use Add Package to create your first prepaid package.",
      )
    ) : (
      <Stack gap="sm">{packagesList.map((pkg) => renderCard(pkg))}</Stack>
    );

  const renderSpecialsTab = () =>
    specialsList.length === 0 ? (
      renderEmptyState(
        "No specials yet. Use Add Special to run your first time-boxed promotion.",
      )
    ) : (
      <Stack gap="lg">
        {SPECIAL_SECTIONS.map(({ status, label }) => {
          const group = specialsByStatus[status];
          if (group.length === 0) return null;
          return (
            <Stack key={status} gap="sm">
              <Title order={5} fw={600}>
                {label}
              </Title>
              {group.map((pkg) => renderCard(pkg, status === "past"))}
            </Stack>
          );
        })}
      </Stack>
    );

  return (
    <Stack p="md">
      <LoadingOverlay visible={isLoading} />

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error"
          color="red"
          withCloseButton
          onClose={() => setError(null)}
        >
          <Group justify="space-between" align="center">
            <Text size="sm">{error}</Text>
            {error === LOAD_ERROR && (
              <Button
                size="compact-xs"
                variant="light"
                color="red"
                onClick={retryPackages}
              >
                Retry
              </Button>
            )}
          </Group>
        </Alert>
      )}

      <Group justify="space-between" align="center">
        <Title order={3} fw={500}>
          Packages & Specials
        </Title>

        {canEdit && (
          <Menu shadow="md" position="bottom-end" withinPortal>
            <Menu.Target>
              <Button
                color="var(--brand-teal)"
                leftSection={<IconPlus size={16} />}
              >
                {activeTab === "specials" ? "Add Special" : "Add Package"}
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {ADD_OPTIONS.map((opt) => (
                <Menu.Item key={opt.type} onClick={() => openCreate(opt.type)}>
                  <Stack gap={2}>
                    <Text size="sm" fw={600}>
                      {opt.label}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {opt.description}
                    </Text>
                  </Stack>
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>

      <Tabs value={activeTab} onChange={handleTabChange}>
        <Tabs.List>
          <Tabs.Tab value="packages">
            Packages
            <Badge ml={8} size="xs" variant="default" color="gray" radius="sm">
              {packagesList.length}
            </Badge>
          </Tabs.Tab>
          <Tabs.Tab value="specials">
            Specials
            <Badge ml={8} size="xs" variant="default" color="gray" radius="sm">
              {specialsList.length}
            </Badge>
          </Tabs.Tab>
        </Tabs.List>
      </Tabs>

      <Text size="sm" c="dimmed">
        {activeTab === "specials" ? specialsDescription : packagesDescription}
      </Text>

      <Box>
        {activeTab === "specials" ? renderSpecialsTab() : renderPackagesTab()}
      </Box>

      {editor && (
        <PackageEditorModal
          opened
          onClose={() => setEditor(null)}
          services={services || []}
          kind={editor.kind}
          offerType={editor.offerType}
          initialPackage={editor.initialPackage}
          duplicateSource={editor.duplicateSource}
          onSubmit={handleEditorSubmit}
          isSaving={isSaving}
        />
      )}

      <DeleteConfirmationModal
        isOpen={!!archivingPackage}
        onClose={() => setArchivingPackage(null)}
        onConfirm={handleArchive}
        isLoading={isSaving}
        title={
          archivingPackage?.kind === "special"
            ? "Archive Special"
            : "Archive Package"
        }
        message={
          archivingPackage
            ? `Are you sure you want to archive "${archivingPackage.name}"? It will no longer be available for sale.`
            : ""
        }
      />
    </Stack>
  );
};

export default PackagesSettingsTab;
